<template>
  <div class="protection-page">
    <div class="unified-container">
      <!-- 頁首 -->
      <div class="page-header">
        <img
          src="@/assets/image/Vector (10).webp"
          alt="衛教專區圖標"
          class="header-icon"
        />
        <h1 class="page-title">
          <span class="title-main">衛教專區</span>
          <span class="title-sub">公貓泌尿道導尿與損傷預防</span>
        </h1>

        <!-- 手機版浮動目錄按鈕 -->
        <button class="menu-toggle-btn" @click="isMenuOpen = !isMenuOpen">
          <span class="menu-icon">☰</span>
          <span class="menu-text">目錄</span>
        </button>
      </div>

      <div class="content-wrapper">
        <!-- 手機版背景遮罩 -->
        <div
          class="overlay"
          v-if="isMenuOpen"
          @click="isMenuOpen = false"
        ></div>

        <!-- 左側導覽 -->
        <div
          class="sidebar"
          :class="{ 'sidebar-mobile': true, open: isMenuOpen }"
        >
          <div class="menu-container">
            <!-- ✅ 新增關閉按鈕 -->
            <button class="menu-close-btn" @click="isMenuOpen = false">
              <span class="close-icon">✕</span>
            </button>
            <h2 class="menu-title">目次</h2>

            <!-- 公貓泌尿道導尿與損傷預防 -->
            <div class="menu-item">
              <button
                class="menu-button"
                :class="{ active: activeMenu === 'catheter' }"
                @click="toggleMenu('catheter')"
              >
                <span class="menu-arrow">▶</span>
                公貓泌尿道導尿與損傷預防
              </button>

              <div class="submenu" v-show="activeMenu === 'catheter'">
                <button
                  class="submenu-item"
                  :class="{ active: isSectionActive('catheter-trauma') }"
                  @click="scrollToSection('catheter-trauma')"
                >
                  造成泌尿道損傷的兩大來源
                </button>

                <button
                  class="submenu-item"
                  :class="{ active: isSectionActive('catheter-technique') }"
                  @click="scrollToSection('catheter-technique')"
                >
                  降低損傷的正確導尿操作技巧
                </button>

                <button
                  class="submenu-item"
                  :class="{ active: isSectionActive('catheter-selection') }"
                  @click="scrollToSection('catheter-selection')"
                >
                  導尿管材質選擇原則
                </button>

                <button
                  class="submenu-item"
                  :class="{ active: isSectionActive('catheter-video') }"
                  @click="scrollToSection('catheter-video')"
                >
                  教學影片
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 右側主要內容 -->
        <div class="main-content">
          <!-- 公貓泌尿道導尿與損傷預防 -->
          <section class="content-section">
            <div class="content-header">
              <h2 class="content-title">公貓泌尿道導尿與損傷預防</h2>
            </div>

            <!-- 一、造成泌尿道損傷的兩大來源 -->
            <div id="catheter-trauma" class="section-content">
              <h3 class="section-title">造成泌尿道損傷的兩大來源</h3>

              <!-- 摩擦損傷 -->
              <div class="subsection-block">
                <h4 class="subsection-title">1. 摩擦損傷（Abrasive Trauma）</h4>
                <ul class="content-list">
                  <li>公貓常因結晶或沙石堵塞遠端尿道，導致尿液無法排出。</li>
                  <li>推開堵塞物後，尿道中仍會殘留大量結晶顆粒。</li>
                  <li>
                    當導尿管插入時，結晶會在狹窄空間內摩擦尿道壁，造成黏膜損傷。
                  </li>
                </ul>
              </div>

              <!-- 拐彎損傷 -->
              <div class="subsection-block">
                <h4 class="subsection-title">
                  2. 拐彎處損傷（Angulation Trauma）
                </h4>
                <ul class="content-list">
                  <li>公貓尿道具有明顯的 S 型曲線。</li>
                  <li>
                    若使用硬質導尿管，末端容易在彎曲處頂撞尿道壁造成傷害。
                  </li>
                  <li>即使將陰莖口附近尿道拉直，遠端第二段 S 彎仍難操作。</li>
                </ul>
              </div>
            </div>

            <!-- 二、降低損傷的正確導尿操作技巧 -->
            <div id="catheter-technique" class="section-content">
              <h3 class="section-title">降低損傷的正確導尿操作技巧</h3>

              <!-- 沖洗減少摩擦 -->
              <div class="subsection-block">
                <h4 class="subsection-title">1. 減少摩擦損傷的沖洗方式</h4>
                <p class="content-text">
                  在置入導尿管的同時，持續以生理食鹽水沖洗。
                </p>
                <ul class="content-list">
                  <li>助手協助固定並拉直尿道。</li>
                  <li>沖洗可把殘留結晶沖回膀胱，避免黏膜摩擦損傷。</li>
                  <li>導尿管進入後再次沖洗，可進一步清除尿道內結晶。</li>
                </ul>
              </div>

              <!-- 拉直尿道 -->
              <div class="subsection-block">
                <h4 class="subsection-title">2. 減少拐彎損傷的技巧</h4>
                <ul class="content-list">
                  <li>助手需協助將靠近陰莖口的尿道拉直後再置入。</li>
                  <li>拉直可減少導尿管在 S 彎處撞擊尿道的機率。</li>
                  <li>
                    但需注意：遠端第二段 S 彎仍無法完全拉直，是導尿的主要難點。
                  </li>
                </ul>
              </div>
            </div>

            <!-- 三、導尿管材質選擇原則 -->
            <div id="catheter-selection" class="section-content">
              <h3 class="section-title">導尿管材質選擇原則</h3>

              <!-- 留置導尿 -->
              <div class="subsection-block">
                <h4 class="subsection-title">
                  1. 適用於留置（長期放置）的導尿管
                </h4>
                <ul class="content-list">
                  <li>
                    <strong>必須選用柔軟材質：</strong
                    >矽膠（Silicone）或聚氨酯（Polyurethane）。
                  </li>
                  <li>柔軟可彎曲，顯著降低對尿道、膀胱壁的損傷。</li>
                  <li>韌性佳，不易折曲，提高留置穩定性。</li>
                </ul>
              </div>

              <!-- 沖洗用導尿 -->
              <div class="subsection-block">
                <h4 class="subsection-title">2. 適用於沖洗的導尿管</h4>
                <ul class="content-list">
                  <li>較硬或容易折斷的導尿管僅適合短暫沖洗用。</li>
                  <li>
                    <strong
                      >不適合留置，否則易造成尿道長期磨損或壓迫傷害。</strong
                    >
                  </li>
                </ul>
              </div>
            </div>

            <!-- 影片區 -->
            <div id="catheter-video" class="section-content">
              <h3 class="section-title">教學影片</h3>

              <div class="video-container">
                <div class="video-grid">
                  <div
                    class="video-card"
                    v-for="video in videos"
                    :key="video.id"
                  >
                    <div class="video-wrapper">
                      <iframe
                        class="video-iframe"
                        :src="getYoutubeEmbedUrl(video.youtubeId)"
                        :title="video.title"
                        frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowfullscreen
                      ></iframe>
                    </div>

                    <div class="video-info">
                      <h4 class="video-title">{{ video.title }}</h4>
                      <p class="video-duration">{{ video.duration }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 四、重點整理 -->
            <div id="catheter-summary" class="section-content">
              <h3 class="section-title">重點整理</h3>

              <div class="notice-box">
                <p class="notice-title">導尿安全關鍵重點</p>
                <ul class="content-list">
                  <li>🐾 導尿前需先沖洗尿道，減少摩擦性損傷。</li>
                  <li>🐾 插入時保持尿道拉直，降低彎曲處損傷。</li>
                  <li>🐾 長期留置請務必使用柔軟材質的導尿管。</li>
                  <li>🐾 導尿技巧比導尿管開口設計更重要。</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ==================== Mobile First 設計 ==================== */

.protection-page {
  padding: 16px;
  min-height: 100vh;
}

.unified-container {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* ==================== 頁首區域 ==================== */

.page-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  gap: 12px;
  position: relative;
}

.header-icon {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.page-title {
  margin: 0;
  color: #143c64;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  flex: 1;
}

.title-main {
  font-size: clamp(1.375rem, 3vw, 1.625rem);
  font-weight: bold;
}

.title-sub {
  font-size: clamp(1rem, 2vw, 1.125rem);
  font-weight: bold;
  color: #5a8ab8;
}

/* ==================== 手機版浮動目錄按鈕 ==================== */

.menu-toggle-btn {
  position: fixed;
  top: 180px;
  right: 16px;
  z-index: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #143c64;
  color: white;
  border: none;
  border-radius: 999px;
  padding: 10px 16px;
  font-size: clamp(0.875rem, 2vw, 0.9375rem);
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
}

.menu-toggle-btn:active {
  transform: scale(0.95);
}

.menu-icon {
  font-size: clamp(1rem, 2.5vw, 1.125rem);
  line-height: 1;
}

.menu-text {
  font-size: clamp(0.875rem, 2vw, 0.875rem);
}

/* ==================== 內容包裹層 ==================== */

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ==================== 手機版背景遮罩 ==================== */

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  z-index: 400;
  transition: opacity 0.3s ease;
}

/* ==================== 側邊欄 (手機版) ==================== */

.sidebar-mobile {
  position: fixed;
  top: 0;
  right: 0;
  width: 280px;
  height: 100%;
  background: white;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.2);
  z-index: 500;
  overflow-y: auto;
  transform: translateX(100%);
  opacity: 0;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.sidebar-mobile.open {
  transform: translateX(0);
  opacity: 1;
}

.menu-container {
  padding: 20px;
  margin-top: 45%;
  position: relative;
}

/* ✅ 新增關閉按鈕樣式 */
.menu-close-btn {
  position: absolute;
  margin-top: -2rem;
  right: 16px;
  background: #e74c3c;
  color: white;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(231, 76, 60, 0.3);
  transition: all 0.2s ease;
  z-index: 10;
}

.close-icon {
  font-size: clamp(1.125rem, 2.5vw, 1.25rem);
  line-height: 1;
  font-weight: bold;
}

.menu-title {
  font-size: clamp(1.125rem, 2.5vw, 1.25rem);
  font-weight: bold;
  color: #143c64;
  margin: 0 0 20px 0;
  text-align: center;
  padding-bottom: 12px;
  border-bottom: 2px solid #5aa0c8;
}

.menu-item {
  margin-bottom: 8px;
}

.menu-button {
  width: 100%;
  padding: 14px 16px;
  background: none;
  border: none;
  text-align: left;
  font-size: clamp(0.875rem, 2vw, 1rem);
  color: #143c64;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 8px;
  transition: all 0.2s ease;
  font-weight: 500;
}

.menu-button:hover {
  background-color: #f0f7ff;
}

.menu-button.active {
  background-color: #e3f2fd;
  font-weight: bold;
  color: #1e4d87;
}

.menu-arrow {
  font-size: clamp(0.75rem, 1.5vw, 0.75rem);
  transition: transform 0.2s ease;
  color: #5aa0c8;
}

.menu-button.active .menu-arrow {
  transform: rotate(90deg);
}

.submenu {
  margin-left: 28px;
  margin-top: 8px;
  padding-left: 12px;
  border-left: 2px solid #e3f2fd;
}

.submenu-item {
  width: 100%;
  padding: 10px 12px;
  background: none;
  border: none;
  text-align: left;
  font-size: clamp(0.875rem, 2vw, 0.875rem);
  color: #143c64;
  cursor: pointer;
  border-radius: 6px;
  margin-bottom: 4px;
  transition: all 0.2s ease;
}

.submenu-item:hover {
  background-color: #f5f5f5;
  color: #1e4d87;
}

.submenu-item.active,
button.submenu-item.active {
  background-color: #e8f4fd !important;
  color: #1e4d87 !important;
  font-weight: 600 !important;
  border-left: 3px solid #5aa0c8 !important;
  padding-left: 9px !important;
}

/* ==================== 主要內容區域 ==================== */

.main-content {
  background: white;
  padding: 0;
}

.content-section {
  margin-bottom: 48px;
  padding-bottom: 32px;
  border-bottom: 1px solid #f0f0f0;
}

.content-section:last-child {
  margin-bottom: 0;
  border-bottom: none;
}

.content-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 3px solid #5aa0c8;
}

.content-title {
  font-size: clamp(1.125rem, 2.5vw, 1.5rem);
  font-weight: bold;
  color: #143c64;
  margin: 0;
}

.section-content {
  margin-bottom: 32px;
  scroll-margin-top: 100px;
}

.section-content:target {
  padding: 20px;
  margin: -4px;
  border-left: 4px solid #5aa0c8;
  background: linear-gradient(90deg, #f8f9ff 0%, transparent 100%);
  border-radius: 0 8px 8px 0;
}

.section-title {
  font-size: clamp(1rem, 2.5vw, 1.5625rem);
  font-weight: bold;
  color: #143c64;
  margin: 0 0 16px 0;
  padding-left: 12px;
  border-left: 4px solid #ffd966;
}

.content-text {
  font-size: clamp(0.9375rem, 2vw, 1.125rem);
  line-height: 1.8;
  color: #333;
  margin-bottom: 12px;
}

.content-list {
  list-style: none;
  padding: 0;
  margin: 16px 0;
}

.content-list li {
  font-size: clamp(0.9375rem, 2vw, 1.125rem);
  line-height: 1.8;
  color: #333;
  margin-bottom: 10px;
  padding-left: 8px;
}

/* ==================== 子章節區塊 ==================== */

.subsection-block {
  margin-bottom: 24px;
}

.subsection-title {
  font-size: clamp(1rem, 2vw, 1.25rem);
  font-weight: bold;
  color: #1e4d87;
  margin: 0 0 12px 0;
  padding-left: 8px;
}

/* ==================== 治療方式區塊 ==================== */

.treatment-box {
  background: #f8f9ff;
  border-left: 4px solid #5aa0c8;
  border-radius: 0 8px 8px 0;
  padding: 16px;
  margin-bottom: 16px;
}

.treatment-subtitle {
  font-size: clamp(0.9375rem, 2vw, 1.0625rem);
  font-weight: bold;
  color: #143c64;
  margin: 0 0 12px 0;
}

/* ==================== 類型區塊 ==================== */

.type-box {
  background: #f8f9ff;
  border-left: 4px solid #5aa0c8;
  border-radius: 0 8px 8px 0;
  padding: 14px;
  margin-bottom: 12px;
}

.type-box.danger {
  background: #fff8f0;
  border-left: 4px solid #e74c3c;
}

.type-title {
  font-size: clamp(0.9375rem, 2vw, 0.9375rem);
  font-weight: bold;
  color: #143c64;
  margin: 0 0 8px 0;
}

/* ==================== 症狀區塊 ==================== */

.symptom-box {
  background: #fff8f0;
  border-left: 4px solid #ffb347;
  border-radius: 0 8px 8px 0;
  padding: 12px;
  margin-bottom: 10px;
}

.symptom-item {
  font-size: clamp(0.9375rem, 2vw, 0.9375rem);
  line-height: 1.8;
  color: #333;
  margin: 0;
}

/* ==================== 預防項目 ==================== */

.prevention-item {
  margin-bottom: 20px;
  padding: 14px;
  background: #f8f9ff;
  border-radius: 8px;
  border-left: 3px solid #5aa0c8;
}

.prevention-title {
  font-size: clamp(0.9375rem, 2vw, 0.9375rem);
  font-weight: bold;
  color: #143c64;
  margin: 0 0 10px 0;
}

/* ==================== 提示框 ==================== */

.notice-box {
  background: linear-gradient(135deg, #fff3cd 0%, #fff8e1 100%);
  border: 2px solid #ffd966;
  border-radius: 12px;
  padding: 20px;
  margin: 24px 0;
  box-shadow: 0 2px 8px rgba(255, 217, 102, 0.2);
}

.notice-title {
  color: #e74c3c;
  font-weight: bold;
  font-size: clamp(1rem, 2vw, 1.25rem);
  margin-bottom: 12px;
}

.highlight-red {
  color: #e74c3c;
  font-weight: bold;
}

/* ==================== 表格樣式 ==================== */

.summary-table {
  margin: 20px 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.summary-table table {
  width: 100%;
  border-collapse: collapse;
}

.summary-table th {
  background: #5aa0c8;
  color: white;
  font-weight: bold;
  padding: 14px;
  text-align: left;
  font-size: clamp(0.9375rem, 2vw, 0.9375rem);
}

.summary-table td {
  padding: 14px;
  border-bottom: 1px solid #e3f2fd;
  color: #333;
  font-size: clamp(0.875rem, 2vw, 0.875rem);
  line-height: 1.6;
}

.summary-table tbody tr:last-child td {
  border-bottom: none;
}

.summary-table tbody tr:hover {
  background-color: #f8f9ff;
}

/* ==================== 疫苗時程表樣式 ==================== */

.vaccine-table {
  margin: 20px 0;
  border: 2px solid #ffd966;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.vaccine-table table {
  width: 100%;
  border-collapse: collapse;
  text-align: center;
}

.vaccine-table th {
  background: #ffd966;
  color: #143c64;
  font-weight: bold;
  padding: 12px 8px;
  border: 1px solid #ffd966;
  font-size: clamp(0.875rem, 2vw, 0.9375rem);
}

.vaccine-table td {
  padding: 12px 8px;
  border: 1px solid #ffe599;
  color: #333;
  font-size: clamp(0.875rem, 2vw, 0.9375rem);
  line-height: 1.6;
}

.vaccine-table tbody tr:nth-child(even) {
  background-color: #fffbf0;
}

.vaccine-notes {
  list-style: none;
  padding: 0;
  margin: 20px 0;
  color: #333;
  font-size: clamp(0.9375rem, 2vw, 0.9375rem);
  line-height: 1.8;
}

.vaccine-notes li {
  margin-bottom: 8px;
  padding-left: 4px;
}

/* ==================== 平板版 (768px 以上) ==================== */

@media (min-width: 768px) {
  .protection-page {
    padding: 32px 24px;
  }

  .unified-container {
    padding: 32px;
  }

  .page-header {
    margin-bottom: 32px;
  }

  .header-icon {
    width: 40px;
    height: 40px;
  }

  .title-main {
    font-size: 1.625rem;
  }

  .title-sub {
    font-size: 1.125rem;
  }

  .content-wrapper {
    flex-direction: row;
    gap: 32px;
    align-items: flex-start;
  }

  .menu-toggle-btn {
    display: none;
  }

  .sidebar-mobile {
    position: sticky;
    top: 120px;
    display: block;
    transform: none !important;
    opacity: 1 !important;
    box-shadow: none;
    width: auto;
    height: auto;
    flex: 0 0 280px;
    max-height: calc(100vh - 140px);
    overflow-y: auto;
  }

  .menu-container {
    border: 3px solid #5aa0c8;
    border-radius: 16px;
    padding: 24px;
  }

  .main-content {
    flex: 1;
    min-width: 0;
  }

  .content-title {
    font-size: 1.375rem;
  }

  .section-title {
    font-size: 1.1875rem;
  }

  .content-text,
  .content-list li {
    font-size: 1.125rem;
  }

  .subsection-title {
    font-size: 1.25rem;
  }

  .treatment-subtitle {
    font-size: 1.0625rem;
  }

  .vaccine-table th,
  .vaccine-table td {
    padding: 14px 12px;
    font-size: 0.9375rem;
  }

  .menu-close-btn {
    display: none;
  }
}

/* ==================== 桌面版 (1024px 以上) ==================== */

@media (min-width: 1024px) {
  .unified-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 40px;
    margin-bottom: -2rem;
  }

  .sidebar-mobile {
    flex: 0 0 320px;
    top: 100px;
    margin-bottom: 60px;
  }

  .menu-container {
    padding: 28px;
  }

  .content-title {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1.5625rem;
  }
  
  .notice-title {
    color: #e74c3c;
    font-weight: bold;
    font-size: 1.25rem;
    margin-bottom: 12px;
  }

  .submenu-item,
  button.submenu-item {
    font-size: 0.875rem;
  }

  .submenu-item.active,
  button.submenu-item.active {
    font-size: 0.875rem;
  }
}
</style>



<script>
import _ from "lodash";

export default {
  name: "DiseasePage",
  data() {
    return {
      activeMenu: "catheter",
      isMenuOpen: false,
      navbarHeight: 120,
      activeSection: "",
      videos: [
        {
          id: "video1",
          title: "模擬公貓導尿及應對方法",
          youtubeId: "N5H7-foS1sA",
        },
      ],
    };
  },
  async mounted() {
    try {
      window.addEventListener("scroll", this.handleScroll);
      this.handleScroll();

      // 🔗 等待 hash 導航處理完成
      await this.handleHashNavigation();

      // ✅ 給一點時間讓選單展開動畫完成
      setTimeout(() => {
        this.$nextTick(() => {
          if (typeof document !== "undefined") {
            console.log("衛教專區（含 hash 導航）載入完成，觸發預渲染事件");
            document.dispatchEvent(new Event("render-event"));
          }
        });
      }, 500); // 給展開動畫 500ms
    } catch (error) {
      console.error("衛教頁面載入失敗:", error);

      this.$nextTick(() => {
        if (typeof document !== "undefined") {
          document.dispatchEvent(new Event("render-event"));
        }
      });
    }
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    toggleMenu(menuName) {
      this.activeMenu = this.activeMenu === menuName ? null : menuName;
    },

    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset =
          element.getBoundingClientRect().top +
          window.scrollY -
          this.navbarHeight;

        window.scrollTo({
          top: offset,
          behavior: "smooth",
        });

        this.isMenuOpen = false;
        this.activeSection = sectionId;
        this.autoExpandMenu(sectionId);

        // 桌面版：保持側欄 active 置中
        if (window.innerWidth > 992) {
          this.$nextTick(() => {
            const activeItem = document.querySelector(".submenu-item.active");
            const sidebar = document.querySelector(".sidebar");
            if (activeItem && sidebar) {
              const sidebarRect = sidebar.getBoundingClientRect();
              const itemRect = activeItem.getBoundingClientRect();
              const offset =
                itemRect.top -
                sidebarRect.top -
                sidebar.clientHeight / 2 +
                itemRect.height / 2;
              sidebar.scrollBy({ top: offset, behavior: "smooth" });
            }
          });
        }
      }
    },

    handleScroll: _.throttle(function () {
      const sections = [
        "catheter-trauma",
        "catheter-technique",
        "catheter-selection",
        "catheter-video",
      ];

      const scrollPosition = window.scrollY + window.innerHeight / 3;

      let foundSection = "";
      for (let i = 0; i < sections.length; i++) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          foundSection = sections[i];
        }
      }

      if (foundSection && foundSection !== this.activeSection) {
        this.activeSection = foundSection;
        this.autoExpandMenu(foundSection);

        if (window.innerWidth > 992) {
          this.$nextTick(() => {
            const activeItem = document.querySelector(".submenu-item.active");
            const sidebar = document.querySelector(".sidebar");
            if (activeItem && sidebar) {
              const sidebarRect = sidebar.getBoundingClientRect();
              const itemRect = activeItem.getBoundingClientRect();
              const offset =
                itemRect.top -
                sidebarRect.top -
                sidebar.clientHeight / 2 +
                itemRect.height / 2;
              sidebar.scrollBy({ top: offset, behavior: "smooth" });
            }
          });
        }
      }
    }, 150),

    autoExpandMenu(sectionId) {
      if (
        [
          "catheter-trauma",
          "catheter-technique",
          "catheter-selection",
          "catheter-video",
        ].includes(sectionId)
      ) {
        this.activeMenu = "catheter";
      }
    },
    isSectionActive(sectionId) {
      return this.activeSection === sectionId;
    },

    // ✅ 新增：處理 hash 導航
    handleHashNavigation() {
      const hash = this.$route.hash.replace("#", "");

      // 根據 hash 值展開對應的選單
      const menuMap = {
        catheter: "catheter",
      };

      if (menuMap[hash]) {
        this.activeMenu = menuMap[hash];

        // 延遲滾動到第一個子區塊,確保頁面已渲染
        this.$nextTick(() => {
          setTimeout(() => {
            const firstSectionMap = {
              catheter: "catheter-trauma",
            };

            const targetSection = firstSectionMap[hash];
            if (targetSection) {
              this.scrollToSection(targetSection);
            }
          }, 300);
        });
      }
    },

    getYoutubeEmbedUrl(videoId) {
      return `https://www.youtube-nocookie.com/embed/${videoId}`;
    },
  },
};
</script>
